import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { shouldHandleClickEventInternally } from './ScribeLinkUtils';

/**
 * A <ScribeLink> should be used to create the <a> elements if we do not know whether the href is an internal
 * or external location (with the different protocol, location or hostname), which is the case e.g. for links
 * configured in Scribe. The external links do not work with react-router, hence we must detect these and let
 * browser handle the navigation.
 *
 * For the internal links (all generated in code, both for Raven and Webshop), please use standard <Link>
 * component from the react-router library.
 */
export default class ScribeLink extends PureComponent {
  static propTypes = {
    to: PropTypes.string.isRequired,
    activeStyle: PropTypes.object,
    activeClassName: PropTypes.string,
    children: PropTypes.node,
    onlyActiveOnIndex: PropTypes.bool,
    onClick: PropTypes.func,
    target: PropTypes.oneOf([
      '_blank', '_self', '_parent', '_top',
    ]),
  };

  static contextTypes = {
    router: PropTypes.object, // optional, available only when rendered inside the context
  };

  /**
   * @param event {MouseEvent}
   */
  handleClick = (event) => {
    if (this.props.onClick) {
      this.props.onClick(event);
    }

    if (!event.defaultPrevented && shouldHandleClickEventInternally(event)) {
      event.preventDefault();
      const anchorTag = event.currentTarget || event.target;
      this.context.router.push(`${anchorTag.pathname}${anchorTag.search}`);
    }
  };

  render() {
    const { children, to, activeClassName, activeStyle, onlyActiveOnIndex, ...props } = this.props;
    const { router } = this.context;

    // ignore if rendered outside the context of router, simplifies unit testing
    // logic copied from from react-router's Link, so we can use consistent APIs
    if (router && to) {
      props.href = router.createHref(to);

      if ((activeClassName || !isEmpty(activeStyle)) && router.isActive(to, onlyActiveOnIndex)) {
        if (activeClassName) {
          if (props.className) {
            props.className += ` ${activeClassName}`;
          } else {
            props.className = activeClassName;
          }
        }

        if (activeStyle) {
          props.style = { ...props.style, ...activeStyle };
        }
      }
    }

    /* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
    return (
      <a
        {...props}
        onClick={this.handleClick}
      >
        {children}
      </a>
    );
    /* eslint-enable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
  }
}
